import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { PipelineCount, PipelineResponse, Stage } from './types'

const PIPELINE_PATH = '/api/procurement/cofa-pipeline'

/** Stages considered "in flight" for the purposes of faster polling —
 *  items the user is actively watching for a state transition. */
const IN_FLIGHT_STAGES: ReadonlySet<Stage> = new Set<Stage>([
  'generation_queued',
  'generation_running',
  'awaiting_approval',
])

export function useCofaPipeline() {
  return useQuery({
    queryKey: ['cofa-pipeline', 'list'],
    queryFn: () =>
      jsonFetch<PipelineResponse>(`${PIPELINE_PATH}?stage=all&limit=500`),
    staleTime: 15_000,
    // Sprint 04 Chunk C: speed up polling while items are in-flight so
    // approved/rejected/failed transitions appear quickly; revert to
    // calm 60s cadence once the pipeline is idle. Matches the legacy
    // reports-portal UX where the user wasn't left watching a stale
    // kanban for a full minute after hitting Approve.
    refetchInterval: (query) => {
      const data = query.state.data as PipelineResponse | undefined
      if (!data?.items) return 60_000
      const anyInFlight = data.items.some((it) => IN_FLIGHT_STAGES.has(it.stage))
      return anyInFlight ? 5_000 : 60_000
    },
  })
}

export function useCofaPipelineCount() {
  return useQuery({
    queryKey: ['cofa-pipeline', 'count'],
    queryFn: () => jsonFetch<PipelineCount>(`${PIPELINE_PATH}/count`),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

function useAdvanceMutation(action: 'approve' | 'reject' | 'retry') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pipelineId: number) =>
      jsonFetch<unknown>(`${PIPELINE_PATH}/${pipelineId}/${action}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cofa-pipeline'] })
    },
  })
}

export function useApprovePipelineItem() {
  return useAdvanceMutation('approve')
}

export function useRejectPipelineItem() {
  return useAdvanceMutation('reject')
}

export function useRetryPipelineItem() {
  return useAdvanceMutation('retry')
}

// 2026-05-01 reassign hook + vendor-products lookup live in
// `@/shell/services/cofa-registry/api/queries.ts` so PipelineDrawer
// (this module) and CofaPreviewDrawer (shell/services) share the same
// implementation. Re-export here is unnecessary — consumers import
// from the shell directly.
